import {computed, ref, Ref, watch} from 'vue'
import type {
  DataColumn,
  DataColumnFilter,
  DataColumnFilterValue,
  DataError,
  DataProvider,
  DataRow
} from '@hantera/design-system'
import type {PrincipalListItem, PrincipalsListParams} from '../../../backend/iam.types'
import type iam from '../../../backend/iam'

export class PrincipalsDataProvider implements DataProvider {
  private _rows = ref<DataRow[]>([])
  private _isLoading = ref(false)
  private _allLoaded = ref(false)
  private _totalCount = ref(0)
  private _entries: PrincipalListItem[] = []
  private _lastCursor: string | undefined = undefined
  private _errors = ref<DataError[]>([])
  private _roleEnumValues = ref<Array<{ key: string; label: string }>>([])

  public readonly rows = computed(() => this._rows.value)
  public readonly isLoading = computed(() => this._isLoading.value)
  public readonly allLoaded = computed(() => this._allLoaded.value)
  public readonly errors = computed(() => this._errors.value.length > 0 ? this._errors.value : undefined)

  public readonly columns = computed<DataColumn[]>(() => [
    {
      key: 'name',
      label: this.t('columns.name'),
      valueIdx: 0,
      canSort: true,
      canFilter: true,
      sort: this.filters.value.orderBy?.startsWith('name') 
        ? (this.filters.value.orderBy.includes('desc') ? 'desc' : 'asc')
        : undefined,
      filters: this.filters.value.name ? [{
        type: 'any' as const,
        value: this.filters.value.name
      }] : undefined,
      values: {
        type: 'text',
        isArray: false,
        supportedFilterTypes: ['any' as const],
      },
    },
    {
      key: 'email',
      label: this.t('columns.email'),
      valueIdx: 1,
      canSort: true,
      canFilter: true,
      sort: this.filters.value.orderBy?.startsWith('email')
        ? (this.filters.value.orderBy.includes('desc') ? 'desc' : 'asc')
        : undefined,
      filters: this.filters.value.email ? [{
        type: 'any' as const,
        value: this.filters.value.email
      }] : undefined,
      values: {
        type: 'text',
        isArray: false,
        supportedFilterTypes: ['any' as const],
      },
    },
    {
      key: 'roles',
      label: this.t('columns.roles'),
      valueIdx: 2,
      canFilter: this._roleEnumValues.value.length > 0,
      filters: this.filters.value.roles?.map(role => ({
        type: 'any' as const,
        value: role
      })),
      values: {
        type: 'enum',
        isArray: true,
        supportedFilterTypes: ['any' as const],
        enums: [{
          values: this._roleEnumValues.value
        }]
      },
    },
    {
      key: 'status',
      label: this.t('columns.status'),
      valueIdx: 3,
      canFilter: false,
      values: {
        type: 'enum',
        isArray: false,
        enums: [
          {
            values: [
              { key: 'active', label: this.t('status.active'), hue: 120 },
              { key: 'suspended', label: this.t('status.suspended'), hue: 0 },
            ],
          },
        ],
      },
    },
    {
      key: 'lastActiveAt',
      label: this.t('columns.lastActive'),
      valueIdx: 4,
      canFilter: false,
      values: {
        type: 'instant',
      },
    },
  ])

  public readonly totalCount = computed(() => {
    return {
      count: this._totalCount.value,
    }
  })

  constructor(
    private filters: Ref<PrincipalsListParams>,
    private iamService: ReturnType<typeof iam>,
    private t: (key: string) => string,
  ) {
    // Load role enum values
    this.loadRoleEnumValues()

    // Watch filters and refresh when they change
    watch(
      filters,
      () => {
        this.refresh()
      },
      { deep: true },
    )
  }

  private async loadRoleEnumValues(): Promise<void> {
    try {
      const response = await this.iamService.getRoles({
        limit: 1000,
        includeSystem: true,
      })
      
      this._roleEnumValues.value = response.roles.map((role) => ({
        key: role.key,
        label: role.key,
      }))
    } catch (error) {
      console.error('Failed to load roles for filtering:', error)
      this._roleEnumValues.value = []
    }
  }

  public async refresh(): Promise<void> {
    this._isLoading.value = true
    this._errors.value = []
    this._entries = []
    this._rows.value = []
    this._lastCursor = undefined
    this._allLoaded.value = false

    try {
      const response = await this.iamService.getPrincipals({
        ...this.filters.value,
        after: undefined,
        before: undefined,
      })

      this._entries = response.principals
      this._rows.value = this._entries.map((p) => this.entryToRow(p))
      this._lastCursor = response.lastCursor
      this._totalCount.value = response.totalCount
      this._allLoaded.value = this._rows.value.length == response.totalCount
    } catch (error: any) {
      console.error('Failed to load principals:', error)
      this._errors.value = [
        {
          code: error.response?.status?.toString() || 'UNKNOWN',
          message: error.response?.data?.message || this.t('errors.failedToLoadUsers'),
        },
      ]
    } finally {
      this._isLoading.value = false
    }
  }

  public async loadMore(): Promise<void> {
    if (this._isLoading.value || this._allLoaded.value || !this._lastCursor) {
      return
    }

    this._isLoading.value = true
    this._errors.value = []

    try {
      const response = await this.iamService.getPrincipals({
        ...this.filters.value,
        after: this._lastCursor,
        before: undefined,
      })

      this._entries.push(...response.principals)
      this._rows.value = this._entries.map((p) => this.entryToRow(p))
      this._lastCursor = response.lastCursor
      this._allLoaded.value = this._rows.value.length == response.totalCount
    } catch (error: any) {
      console.error('Failed to load more principals:', error)
      this._errors.value = [
        {
          code: error.response?.status?.toString() || 'UNKNOWN',
          message: error.response?.data?.message || this.t('errors.failedToLoadMoreUsers'),
        },
      ]
    } finally {
      this._isLoading.value = false
    }
  }

  public setSort(column: DataColumn, direction?: 'asc' | 'desc'): void {
    if (column.key === 'name' || column.key === 'email') {
      this.filters.value.orderBy = direction ? `${column.key}${direction === 'desc' ? ' desc' : ''}` : undefined
    }
  }

  public setFilters(column: DataColumn, filters?: DataColumnFilter[]): void {
    if (column.key === 'roles') {
      const roleKeys = filters
        ?.filter((f): f is DataColumnFilterValue => 'value' in f)
        .map(f => f.value as string) || []
      
      this.filters.value.roles = roleKeys
    } else if (column.key === 'name') {
      const nameFilters = filters?.filter((f): f is DataColumnFilterValue => 'value' in f)
      this.filters.value.name = nameFilters?.length ? 
        nameFilters[nameFilters.length - 1].value as string : undefined
    } else if (column.key === 'email') {
      const emailFilters = filters?.filter((f): f is DataColumnFilterValue => 'value' in f)
      this.filters.value.email = emailFilters?.length ? 
        emailFilters[emailFilters.length - 1].value as string : undefined
    }
  }

  public getRowHue(row: DataRow) {
    return (row as any).hue
  }
  
  private entryToRow(principal: PrincipalListItem): DataRow {
    const status = principal.suspendedAt ? 'suspended' : 'active'

    return <DataRow>{
      key: principal.id,
      hue: principal.suspendedAt ? 0 : undefined,
      values: [
        principal.name || principal.email,
        principal.email, 
        principal.roles, 
        status,
        principal.lastActiveAt
      ],
    }
  }
}
